import { jwtDecode } from 'jwt-decode';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authUrl } from '../config/authUrls';
import { DESIGN_JWT, DESIGN_USER, isDesignMode } from '../config/designMode';

const AuthContext = createContext();

const TOKEN_EXPIRY_THRESHOLD = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => (isDesignMode() ? DESIGN_USER : null));
  const [token, setToken] = useState(() => (isDesignMode() ? DESIGN_JWT : null));

  const isTokenExpired = useCallback((tok) => {
    if (!tok) return true;
    try {
      const decodedToken = jwtDecode(tok);
      return decodedToken.exp * 1000 < Date.now() + TOKEN_EXPIRY_THRESHOLD;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }, []);

  const login = useCallback((userData, authToken) => {
    setToken(authToken);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    if (isDesignMode()) {
      window.location.reload();
      return;
    }
    fetch(authUrl('/logout'), { method: 'POST', credentials: 'include' }).catch(
      () => {}
    );
    setUser(null);
    setToken(null);
  }, []);

  const refreshToken = useCallback(async () => {
    if (isDesignMode()) {
      return DESIGN_JWT;
    }
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(authUrl('/refresh'), {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Token refresh failed');
      }
      const data = await response.json();
      login(data.user, data.token);
      return data.token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      setUser(null);
      setToken(null);
      return null;
    }
  }, [login, token]);

  const getValidToken = useCallback(async () => {
    if (isDesignMode()) {
      return DESIGN_JWT;
    }
    if (token && !isTokenExpired(token)) {
      return token;
    }
    return refreshToken();
  }, [isTokenExpired, refreshToken, token]);

  useEffect(() => {
    if (isDesignMode() || token) {
      return;
    }
    refreshToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ user, token, login, logout, getValidToken }),
    [user, token, login, logout, getValidToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
